import { Injectable } from '@angular/core';
import { Cache, cacheable } from '../cache/index';
import { sevenDaysMilli } from '../constants-types/constants';
import { BehavioralFactorRanges } from '../core-data-model/BehavioralFactorRanges.model';
import {
    ToolboxGuidebookServer,
    RelationshipGuideServer,
    ReferencePatternServer,
    JobFitServer
} from '../core-data-model/Guidebook.interface';
import { RefProfileReportInfo } from '../core-data-model/Job/JobReportInfo.model';
import { Job } from '../core-data-model/Job.model';
import { Pattern } from '../core-data-model/Pattern.model';
import {
    FactorInfo,
    RelationshipGuide
} from '../core-data-model/PatternInsights.interface';
import { JobFit, Person } from '../core-data-model/Person.model';
import {
    ReferencePatternType,
    ReferencePatternsInformation
} from '../core-data-model/ReferencePatternType.model';
import { GuidebookTransformers } from '../core-services/Guidebook.datatransform';
import { ToolboxTransformer } from '../core-services/Toolbox.datatransform';
import { ApiService } from '../system-services/Api.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { TranslationService } from '../translations';

@Injectable({
    providedIn: 'root'
})
export class ToolboxApi {
    // There's only one entry so just return a constant for the hash function
    private guidebookCache = new Cache<FactorInfo[], string>(
        'guidebookCache',
        () => 'cache'
    );
    private patternToReferenceProfileCache = new Cache<
        ReferencePatternType,
        Pattern
    >(
        'patternToReferenceProfileCache',
        (pattern: Pattern) => `${pattern.A}${pattern.B}${pattern.C}${pattern.D}`
    );
    private patternIdToReferenceProfileCache = new Cache<
        RefProfileReportInfo,
        number
    >('patternIdToReferenceProfileCache', (id) => id + '');
    private toolboxTransformer: ToolboxTransformer;

    constructor(
        private api: ApiService,
        private transformer: GuidebookTransformers,
        private userProfile: UserProfileSettingsService,
        private TR: TranslationService
    ) {
        this.toolboxTransformer = new ToolboxTransformer();
    }

    @cacheable(
        function (this: ToolboxApi) {
            return this.guidebookCache;
        },
        () => 'cache',
        null,
        null,
        sevenDaysMilli
    )
    private getGuidebookServer() {
        return this.api.get<ToolboxGuidebookServer>(
            `/api/v3/toolbox/guidebook`
        );
    }

    getGuidebook(): Promise<FactorInfo[]> {
        return this.getGuidebookServer().then(([server]) =>
            server.factors.map((factor) =>
                this.transformer.transformFactorInfo(factor)
            )
        );
    }

    @cacheable(
        function (this: ToolboxApi) {
            return this.patternToReferenceProfileCache;
        },
        (pattern: Pattern) => pattern,
        null,
        null,
        sevenDaysMilli
    )
    getReferenceProfile(pattern: Pattern): Promise<ReferencePatternType> {
        return this.api
            .get<ReferencePatternServer>(
                `/api/v3/toolbox/referenceProfile?A=${pattern.A}&B=${pattern.B}&C=${pattern.C}&D=${pattern.D}`
            )
            .then(([server]) =>
                ReferencePatternsInformation.find(
                    (pat) => pat.id === server.patternNumber
                )
            );
    }

    @cacheable(
        function (this: ToolboxApi) {
            return this.patternIdToReferenceProfileCache;
        },
        (id: number) => id,
        null,
        null,
        sevenDaysMilli
    )
    getReferenceProfileById(patternId: number): Promise<RefProfileReportInfo> {
        if (patternId === null) {
            return Promise.resolve(null);
        }
        return this.api
            .get<ReferencePatternServer>(
                `/api/v3/toolbox/referenceProfileById/${patternId}`
            )
            .then(([server]) =>
                this.toolboxTransformer.transformToRefProfile(server)
            );
    }

    getJobReferenceProfiles(
        ranges: BehavioralFactorRanges
    ): Promise<RefProfileReportInfo[]> {
        return this.api
            .get<
                ReferencePatternServer[]
            >(`/api/v3/toolbox/matchingJobReferenceProfiles?aMin=${ranges.A.min}&bMin=${ranges.B.min}&cMin=${ranges.C.min}&dMin=${ranges.D.min}&aMax=${ranges.A.max}&bMax=${ranges.B.max}&cMax=${ranges.C.max}&dMax=${ranges.D.max}`)
            .then(([response]) =>
                this.toolboxTransformer.transformReferenceProfiles(response)
            );
    }

    getJobTargetReferenceProfiles(
        ranges: BehavioralFactorRanges
    ): Promise<ReferencePatternType[]> {
        return this.api
            .get<
                ReferencePatternServer[]
            >(`/api/v3/toolbox/matchingJobReferenceProfiles?aMin=${ranges.A.min}&bMin=${ranges.B.min}&cMin=${ranges.C.min}&dMin=${ranges.D.min}&aMax=${ranges.A.max}&bMax=${ranges.B.max}&cMax=${ranges.C.max}&dMax=${ranges.D.max}`)
            .then(([response]) =>
                this.toolboxTransformer.transformReferencePatterns(response)
            );
    }

    getRelationshipGuide(
        personId1: string,
        personId2: string,
        languageLocale: string = this.TR.currentLocale,
        companyId = this.userProfile.currentCompany
    ): Promise<RelationshipGuide[]> {
        return this.api
            .get<
                RelationshipGuideServer[]
            >(`/api/v3/companies/${companyId}/persons/${personId1}/relationshipGuide?personId1=${personId2}&languageLocale=${languageLocale}`)
            .then(([serverData]) =>
                serverData.map((oneGuide) =>
                    this.transformer.transformRelationshipGuide(oneGuide)
                )
            );
    }

    async getJobFit(person: Person, job: Job): Promise<JobFit> {
        const self: Pattern = person?.originalAssessment?.pattern?.self;

        const [serverData] = await this.api.get<JobFitServer>(
            `/api/v3/toolbox/jobMatchScore` +
                `?personA=${self?.A}` +
                `&personB=${self?.B}` +
                `&personC=${self?.C}` +
                `&personD=${self?.D}` +
                `&jobLowA=${job?.behavioralRanges?.A.min}` +
                `&jobLowB=${job?.behavioralRanges?.B.min}` +
                `&jobLowC=${job?.behavioralRanges?.C.min}` +
                `&jobLowD=${job?.behavioralRanges?.D.min}` +
                `&jobHiA=${job?.behavioralRanges?.A.max}` +
                `&jobHiB=${job?.behavioralRanges?.B.max}` +
                `&jobHiC=${job?.behavioralRanges?.C.max}` +
                `&jobHiD=${job?.behavioralRanges?.D.max}` +
                `&personCog=${person?.highestCognitiveAssessment?.score}` +
                `&jobCog=${job?.cognitiveTarget}` +
                `&cogWeight=${job?.cognitiveWeight}`
        );
        return new JobFit({
            overallFitScore: serverData.overallFitScore,
            behavioralFitScore: serverData.behavioralFitScore,
            cognitiveFitScore: serverData.cognitiveFitScore
        });
    }
}
